import type { Root } from 'mdast';
import {
  ContainerDirective,
  LeafDirective,
  TextDirective,
} from 'mdast-util-directive';
import type { Plugin } from 'unified';
import { visit } from 'unist-util-visit';

/**
 * Extended data type that includes hast properties for react-markdown
 */
interface DirectiveDataWithHast {
  hName?: string;
  hProperties?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Whitelist of valid directive names that should be handled by other plugins
 */
const VALID_DIRECTIVES = new Set([
  'salesOrder',
  'booking',
  'load',
  'account',
  'purchaseOrder',
  'invoice',
  'bill',
  'inventoryItem',
  'facility',
  'emoji',
  'chart',
  'image',
]);

/**
 * Remark plugin that cleans up unhandled directives by converting them back to plain text
 *
 * This plugin must run AFTER all other directive plugins to catch any directives
 * that weren't processed. This prevents issues where text containing colons
 * (like "2:19 PM") gets incorrectly parsed as directives.
 *
 * The plugin converts unhandled directives back to their original syntax:
 * - Text directive `:name[content]` -> `:name[content]`
 * - Leaf directive `::name{attr="value"}` -> `::name{attr="value"}`
 * - Container directive `:::name` -> `:::name\n...\n:::`
 *
 * @example
 * Input: "The time is 2:19 PM"
 * Without plugin: "The time is 2<div></div> PM" (broken)
 * With plugin: "The time is 2:19 PM" (correct)
 */
export const remarkDirectiveCleanup: Plugin<[], Root> = () => (tree) => {
  const visitDirective = (
    node: ContainerDirective | LeafDirective | TextDirective,
  ) => {
    // Skip if this is a valid directive that was already processed
    if (VALID_DIRECTIVES.has(node.name)) {
      return;
    }

    // Check if the directive was already processed by looking at node.data
    if (node.data && (node.data as DirectiveDataWithHast).hName) {
      return;
    }

    // Convert the unhandled directive back to plain text
    let text = '';

    if (node.type === 'textDirective') {
      // Text directive: :name[content]
      text = `:${node.name}`;

      // Add attributes if present
      if (node.attributes && Object.keys(node.attributes).length > 0) {
        const attrs = Object.entries(node.attributes)
          .map(([key, value]) => `${key}="${value}"`)
          .join(' ');
        text += `{${attrs}}`;
      }

      // Add content if present
      if (node.children && node.children.length > 0) {
        const content = extractTextContent(node);
        text += `[${content}]`;
      }
    } else if (node.type === 'leafDirective') {
      // Leaf directive: ::name{attr="value"}
      text = `::${node.name}`;

      if (node.attributes && Object.keys(node.attributes).length > 0) {
        const attrs = Object.entries(node.attributes)
          .map(([key, value]) => `${key}="${value}"`)
          .join(' ');
        text += `{${attrs}}`;
      }
    } else if (node.type === 'containerDirective') {
      // Container directive: :::name\n...\n:::
      text = `:::${node.name}`;

      if (node.attributes && Object.keys(node.attributes).length > 0) {
        const attrs = Object.entries(node.attributes)
          .map(([key, value]) => `${key}="${value}"`)
          .join(' ');
        text += `{${attrs}}`;
      }

      text += '\n';
      if (node.children && node.children.length > 0) {
        text += extractTextContent(node);
      }
      text += '\n:::';
    }

    // Replace the directive node with a text node
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    node.type = 'text' as any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (node as any).value = text;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete (node as any).name;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete (node as any).attributes;
    node.children = [];
  };

  // Process all directive types
  visit(tree, 'textDirective', visitDirective);
  visit(tree, 'leafDirective', visitDirective);
  visit(tree, 'containerDirective', visitDirective);
};

/**
 * Extract text content from directive children
 */
function extractTextContent(
  node: ContainerDirective | LeafDirective | TextDirective,
): string {
  let text = '';

  function traverse(children: unknown[]): void {
    for (const child of children) {
      if (typeof child === 'object' && child !== null) {
        const childNode = child as {
          type?: string;
          value?: string;
          children?: unknown[];
        };
        if (childNode.type === 'text' && childNode.value) {
          text += childNode.value;
        } else if (childNode.children) {
          traverse(childNode.children);
        }
      }
    }
  }

  if (node.children) {
    traverse(node.children);
  }

  return text;
}
